import { Injectable } from '@angular/core';
import { ApiService } from '../core/services/api.service';
import { Observable } from 'rxjs';
import { ImportFormTemplatesRequest, ProjectFormFolderModel, UpdateStatusRequest, ProjectFormModel, CreateProjectFormRequest, ProjectFormStatusModel, CloneProjectFormRequest, RenameProjectFormRequest } from '../models/projectform.model';

@Injectable({
  providedIn: 'root'
})
export class ProjectFormService {
  private baseUrl = 'ProjectForm';

  constructor(private apiService: ApiService) { }

  private formatDateOnly(value?: string | Date | null): string | undefined {
    if (!value) {
      return undefined;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? undefined
        : this.formatDateParts(value.getFullYear(), value.getMonth() + 1, value.getDate());
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return this.formatDateParts(Number(match[1]), Number(match[2]), Number(match[3]));
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? undefined
      : this.formatDateParts(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate());
  }

  private formatDateParts(year: number, month: number, day: number): string {
    return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  }

  getProjectFormFolders(projectId: number): Observable<ProjectFormFolderModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetProjectFormFolders/${projectId}`);
  }

  importFormTemplates(request: ImportFormTemplatesRequest): Observable<ProjectFormFolderModel[]> {
    return this.apiService.post(`${this.baseUrl}/ImportFormTemplates`, request);
  }

  updateFormFolderStatus(folderId: number, request: UpdateStatusRequest): Observable<any> {
    return this.apiService.put(`${this.baseUrl}/UpdateFormFolderStatus/${folderId}`, request);
  }

  getProjectForms(folderId: number): Observable<ProjectFormModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetProjectForms/${folderId}`);
  }

  getLinkedProjectFormsByTaskId(projectId: number, taskId: number): Observable<ProjectFormModel[]> {
    return this.apiService.get(`${this.baseUrl}/GetLinkedProjectFormsByTaskId/${projectId}/${taskId}`);
  }

  createProjectForm(request: CreateProjectFormRequest): Observable<ProjectFormModel> {
    return this.apiService.post(`${this.baseUrl}/CreateProjectForm`, request);
  }

  cloneProjectForm(request: CloneProjectFormRequest): Observable<ProjectFormModel> {
    return this.apiService.post(`${this.baseUrl}/CloneProjectForm`, request);
  }

  getProjectFormById(projectFormId: number): Observable<ProjectFormModel> {
    return this.apiService.get(`${this.baseUrl}/GetProjectFormById/${projectFormId}`);
  }

  updateProjectFormStatus(formId: number, templateNameId: number, model: ProjectFormStatusModel): Observable<any> {
    const payload: ProjectFormStatusModel = {
      ...model,
      date: this.formatDateOnly(model.date),
      duedate: this.formatDateOnly(model.duedate)
    };

    return this.apiService.put(`${this.baseUrl}/UpdateProjectFormStatus/${formId}/${templateNameId}`, payload);
  }

  renameProjectForm(formId: number, request: RenameProjectFormRequest): Observable<any> {
    return this.apiService.put(`${this.baseUrl}/RenameProjectForm/${formId}`, request);
  }

  deleteProjectForm(formId: number): Observable<any> {
    return this.apiService.delete(`${this.baseUrl}/DeleteProjectForm/${formId}`);
  }
}
